import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../Sidebar";
import { useLocation } from "react-router-dom";
import { FaChartLine } from "react-icons/fa";
import { LayoutDashboard, FileDown, Image } from "lucide-react";
import axios from "axios";
import { getDB, dbGet, dbPut } from "../../db";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import JSZip from "jszip";
import "../sprzedaz/DashboardGrid.css";

import MonthlyPurchaseComparison from "./MonthlyPurchaseComparison";
import QuarterlyPurchaseComparison from "./QuarterlyPurchaseComparison";
import WeekPurchaseDetailsChart from "./WeekPurchaseDetailsChart";
import WeekPurchaseComparisonChart from "./WeekPurchaseComparisonChart";
import DailyPurchaseComparisonChart from "./DailyPurchaseComparisonChart";
import TabelaPorownaniaMiesiecyZakupy from "./TabelaPorownaniaMiesiecyZakupy";
import IdentyfikacjaNietypowychZakupow from "./IdentyfikacjaNietypowychZakupow";
import ScatterPlotZakupy from "./ScatterPlotZakupy";
import AveragePurchasesByWeekday from "./AveragePurchasesByWeekday";
import StrukturaZakupowChart from "./StrukturaZakupowChart.js";
import TopBar from "../TopBar.jsx";
import ZakupyWedlugMiesiecyReport from "./ZakupyWedlugMiesiecyReport.js";
import ZakupyTygodnioweZTrendami from "./ZakupyTygodnioweZTrendami.js";

import { createGlobalStyle } from "styled-components";

// NEW: license-gated DownloadButtons + JWT decode
import { DownloadButtons } from "../DownloadButtons";
import { jwtDecode } from "jwt-decode";

/* ----------------------- EXPORT MODE (generic) ----------------------- */
const ExportModeStyles = createGlobalStyle`
  .for-export * { pointer-events: none !important; cursor: default !important; }
  .for-export [data-export-hide="true"] { display: none !important; }
`;
const enableExportMode = () => document.body.classList.add("for-export");
const disableExportMode = () => document.body.classList.remove("for-export");
/* -------------------------------------------------------------------- */

const cardStyle = {
  border: "1px solid #ccc",
  borderRadius: "8px",
  padding: "20px 20px 40px 20px",
  backgroundColor: "#f9f9f9",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  overflow: "hidden",
  textAlign: "center",
};

const SectionTitle = ({ title, inGrid = false }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "10px 20px 12px 20px",
      margin: inGrid ? "8px 0 12px 0" : "8px 20px 12px 20px",
      fontSize: "1.05rem",
      fontWeight: 700,
      color: "#2b2b2b",
      position: "relative",
      background: "linear-gradient(to right, rgba(235, 135, 227, 0.12), rgba(227, 135, 235, 0.04))",
      border: "1px solid rgba(235, 135, 230, 0.35)",
      borderRadius: "8px",
      ...(inGrid ? { gridColumn: "1 / -1", paddingLeft: "20px", paddingRight: "20px" } : {}),
    }}
  >
    <LayoutDashboard size={20} strokeWidth={2} color="#eb87deff" />
    <span style={{ position: "relative", display: "inline-block" }}>
      {title}
      <span
        style={{
          position: "absolute",
          left: 0,
          bottom: -3,
          width: "100%",
          height: 2,
          backgroundColor: "#eb87beff",
          borderRadius: 2,
          opacity: 0.7,
        }}
      />
    </span>
  </div>
);

const formatPath = (path) => {
  const customNames = {
    "zakupy/daily": { name: "Zakupów / Zakupy wg okresów", icon: <FaChartLine /> },
  };
  const cleanPath = path.replace(/^\//, "");
  return customNames[cleanPath] || { name: cleanPath.replace("/", " / "), icon: null };
};

// Helper to read license from token (supports NO EXPIRE by flag or far-future date)
function extractLicenseFromToken(token) {
  if (!token) return null;
  try {
    const d = jwtDecode(token) || {};
    const typeRaw = d.LicenseType || d.licenseType || d.plan || d.Plan || "";
    const expRaw  = d.LicenseExpire ?? d.licenseExpire ?? "";

    const t = String(typeRaw).toUpperCase();
    const e = String(expRaw).toUpperCase();

    const typeHasNoExp = t.includes("NO EXPIRE") || t.includes("NO_EXPIRE");
    const expIsFlag    = e === "NO EXPIRE" || e === "NO_EXPIRE";

    let yearIsFar = false;
    if (!expIsFlag && typeof expRaw === "string" && expRaw) {
      const dt = new Date(expRaw);
      yearIsFar = !isNaN(dt) && dt.getUTCFullYear() >= 2099;
    }

    if (typeHasNoExp || expIsFlag || yearIsFar) return "NO EXPIRE";
    return typeRaw || null;
  } catch {
    return null;
  }
}

const ZakupyDzien = () => {
  const [email, setEmail] = useState("");
  const [data, setData] = useState([]);
  const locationData = useLocation();
  const { name, icon } = formatPath(locationData.pathname);
  const [loadingComponent, setLoadingComponent] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);

  // NEW: license
  const [licenseType, setLicenseType] = useState(null);

  // Refs (deduped)
  const monthlySalesRef = useRef(null);
  const quarterlySalesRef = useRef(null);
  const weekDetailsRef = useRef(null);

  const weekComparisonRef = useRef(null);
  const dailyComparisonRef = useRef(null);

  const tabelaRef = useRef(null);
  const monthlyReportRef = useRef(null);

  const nietypoweRef = useRef(null);
  const averageSalesRef = useRef(null);

  const scatterPlotRef = useRef(null);

  const strukturaRef = useRef(null);
  const weeklyTrendsRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    if (user?.Email) setEmail(user.Email);

    // pick up license from user and/or token
    const ltUser =
      user?.licenseType ?? user?.LicenseType ?? user?.plan ?? user?.Plan ?? null;
    if (typeof ltUser === "string" && ltUser) setLicenseType(ltUser);

    const token = localStorage.getItem("token");
    const ltFromToken = extractLicenseFromToken(token);
    if (ltFromToken) setLicenseType(ltFromToken);
  }, []);

  const fetchData = async () => {
    try {
      setLoadingComponent("Sprawdzanie pamięci podręcznej zakupów...");
      await getDB();

      const cached = await dbGet("zakupy", "allZakupy");
      if (cached?.data?.length) {
        setData(cached.data);
        setLoadingComponent("Loaded purchase data from local IndexedDB");
        return;
      }

      setLoadingComponent("Brak cache. Pobieranie zakupów z API...");
      const token = localStorage.getItem("token");
      const res = await axios.get("https://server229256.nazwa.pl/api/zakupy", {
        headers: { Authorization: token },
      });

      const purchases = res.data || [];
      setData(purchases);
      await dbPut("zakupy", "allZakupy", { data: purchases, timestamp: Date.now() });
      setLoadingComponent("Pobrano z API i zapisano w IndexedDB");
    } catch (e) {
      console.error("fetchData error:", e);
      setLoadingComponent("Błąd podczas ładowania danych zakupów");
      setData([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ------------ helpers for clean exports (hide in-card controls) ------------ */
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const hideCardControlsTemporarily = (rootEl) => {
    const controls = Array.from(
      rootEl.querySelectorAll(
        'input, select, button, [data-export-hide="true"], .filters, .control-row, .toolbar'
      )
    );
    controls.forEach((el) => {
      el.__prevDisplay = el.style.display;
      el.style.display = "none";
    });
    return () => {
      controls.forEach((el) => {
        el.style.display = el.__prevDisplay || "";
        delete el.__prevDisplay;
      });
    };
  };

  const captureElementAsJpeg = async (el) => {
    el.scrollIntoView({ block: "center" });
    const restore = hideCardControlsTemporarily(el);
    await wait(200);
    const canvas = await html2canvas(el, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      ignoreElements: (node) => {
        if (!(node instanceof Element)) return false;
        return node.matches('[data-export-hide="true"]');
      },
    });
    restore();
    const dataUrl = canvas.toDataURL("image/jpeg", 0.9);
    return { dataUrl, width: canvas.width, height: canvas.height };
  };
  /* -------------------------------------------------------------------------- */

  // === REPORT LAYOUTS ===
  const COMPONENTS_FOR_REPORT = [
    { ref: monthlySalesRef,  title: "Monthly Purchase Comparison",  layout: "landscape-3col" },
    { ref: quarterlySalesRef, title: "Quarterly Purchase Comparison", layout: "landscape-3col" },
    { ref: weekDetailsRef,   title: "Week Purchase Details",        layout: "landscape-3col" },

    { ref: weekComparisonRef,  title: "Week Purchase Comparison",   layout: "landscape-2col" },
    { ref: dailyComparisonRef, title: "Daily Purchase Comparison",  layout: "landscape-2col" },

    { ref: tabelaRef,        title: "Monthly Comparison Table (Zakupy)", layout: "landscape-2col" },
    { ref: monthlyReportRef, title: "Zakupy Według Miesięcy — Report",   layout: "landscape-2col" },

    { ref: nietypoweRef,     title: "Identyfikacja Nietypowych Zakupów", layout: "landscape-2col" },
    { ref: averageSalesRef,  title: "Average Purchases by Weekday",      layout: "landscape-2col" },

    { ref: scatterPlotRef,   title: "Scatter Plot — Zakupy",        layout: "landscape" },

    { ref: strukturaRef,     title: "Struktura Zakupów (Kraj/UE/Eksport)", layout: "landscape-2col" },
    { ref: weeklyTrendsRef,  title: "Zakupy Tygodniowe z Trendami",       layout: "landscape-2col" },
  ];

  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    enableExportMode();
    try {
      const reportTitle = "Zakupy wg okresów — Raport";

      const P_MARGINS = { left: 12, right: 12, top: 22, bottom: 16 };
      const L_MARGINS = { left: 12, right: 12, top: 22, bottom: 16 };
      const pages = [];

      const getPageMetrics = (orient) => {
        const isLandscape = orient === "l" || orient === "l2" || orient === "l3";
        const base = isLandscape
          ? { pageW: 297, pageH: 210, margins: L_MARGINS }
          : { pageW: 210, pageH: 297, margins: P_MARGINS };
        const contentW = base.pageW - base.margins.left - base.margins.right;
        const contentH = base.pageH - base.margins.top - base.margins.bottom;
        return { ...base, contentW, contentH };
      };

      let curY = null;
      let l3colCountOnCurrentPage = 0;
      let l2colCountOnCurrentPage = 0;

      const addBlock = (img, title, layout) => {
        const gutter = 6;

        if (layout === "landscape-3col") {
          if (!pages.length || pages[pages.length - 1].orient !== "l3" || l3colCountOnCurrentPage >= 3) {
            pages.push({ orient: "l3", elements: [] });
            l3colCountOnCurrentPage = 0;
          }
          const { margins, contentW, contentH } = getPageMetrics("l");
          const columns = 3;
          const cellW = (contentW - gutter * (columns - 1)) / columns;
          const cellH = contentH * 0.9;
          const col = l3colCountOnCurrentPage;
          const x = margins.left + col * (cellW + gutter);
          const y = margins.top;
          const ratio = img.height / img.width;
          let w = cellW;
          let h = w * ratio;
          if (h > cellH) { h = cellH; w = h / ratio; }
          pages[pages.length - 1].elements.push({
            x, y: y + (cellH - h) / 2, w, h, title, dataUrl: img.dataUrl,
          });
          l3colCountOnCurrentPage += 1;
          return;
        }

        if (layout === "landscape-2col") {
          if (!pages.length || pages[pages.length - 1].orient !== "l2" || l2colCountOnCurrentPage >= 2) {
            pages.push({ orient: "l2", elements: [] });
            l2colCountOnCurrentPage = 0;
          }
          const { margins, contentW, contentH } = getPageMetrics("l");
          const columns = 2;
          const cellW = (contentW - gutter * (columns - 1)) / columns;
          const cellH = contentH * 0.9;
          const col = l2colCountOnCurrentPage;
          const x = margins.left + col * (cellW + gutter);
          const y = margins.top;
          const ratio = img.height / img.width;
          let w = cellW;
          let h = w * ratio;
          if (h > cellH) { h = cellH; w = h / ratio; }
          pages[pages.length - 1].elements.push({
            x, y: y + (cellH - h) / 2, w, h, title, dataUrl: img.dataUrl,
          });
          l2colCountOnCurrentPage += 1;
          return;
        }

        if (layout === "landscape") {
          pages.push({ orient: "l", elements: [] });
          const { margins, contentW, contentH } = getPageMetrics("l");
          const ratio = img.height / img.width;
          const targetW = contentW;
          const targetH = Math.min(targetW * ratio, contentH * 0.9);
          const startY = margins.top + (contentH - targetH) / 2;
          pages[pages.length - 1].elements.push({
            x: margins.left, y: startY, w: targetW, h: targetH, title, dataUrl: img.dataUrl,
          });
          return;
        }

        // portrait fallback
        const { margins, contentW, contentH } = getPageMetrics("p");
        const targetW = contentW;
        const ratio = img.height / img.width;
        const targetH = Math.min(targetW * ratio, contentH * 0.9);
        if (!pages.length || pages[pages.length - 1].orient !== "p") {
          pages.push({ orient: "p", elements: [] });
          const { margins: m } = getPageMetrics("p");
          curY = m.top;
        }
        if (curY + targetH > margins.top + contentH) {
          pages.push({ orient: "p", elements: [] });
          curY = margins.top;
        }
        pages[pages.length - 1].elements.push({
          x: margins.left, y: curY, w: targetW, h: targetH, title, dataUrl: img.dataUrl,
        });
        curY += targetH + 6;
      };

      for (const block of COMPONENTS_FOR_REPORT) {
        if (!block.ref?.current) continue;
        const img = await captureElementAsJpeg(block.ref.current);
        addBlock(img, block.title, block.layout);
      }

      if (pages.length === 0) pages.push({ orient: "p", elements: [] });

      const firstOrient = pages[0].orient === "p" ? "portrait" : "landscape";
      const pdf = new jsPDF({ orientation: firstOrient, unit: "mm", format: "a4", compress: true });

      const drawHeaderFooter = (pdf, page, pageCount, opts) => {
        const { leftMargin, topMargin, rightMargin, bottomMargin, title } = opts;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.text(title, leftMargin, topMargin - 5, { baseline: "bottom" });
        pdf.setLineWidth(0.2);
        pdf.line(leftMargin, topMargin - 3, pdf.internal.pageSize.getWidth() - rightMargin, topMargin - 3);

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        const pageLabel = `Page ${page} / ${pageCount}`;
        const dateLabel = new Date().toISOString().slice(0, 19).replace("T", " ");
        const y = pdf.internal.pageSize.getHeight() - bottomMargin + 5;
        pdf.text(pageLabel, leftMargin, y);
        const w = pdf.getTextWidth(dateLabel);
        pdf.text(dateLabel, pdf.internal.pageSize.getWidth() - rightMargin - w, y);
      };

      const pageCount = pages.length;
      pages.forEach((pg, i) => {
        if (i > 0) {
          const ori = pg.orient === "p" ? "portrait" : "landscape";
          pdf.addPage("a4", ori);
        }
        const { margins } = getPageMetrics(pg.orient);
        drawHeaderFooter(pdf, i + 1, pageCount, {
          leftMargin: margins.left,
          rightMargin: margins.right,
          topMargin: margins.top,
          bottomMargin: margins.bottom,
          title: reportTitle,
        });
        pg.elements.forEach((el) => {
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(10);
          const titleY = el.y - 2;
          if (titleY > margins.top) pdf.text(el.title, el.x, titleY);
          pdf.addImage(el.dataUrl, "JPEG", el.x, el.y, el.w, el.h, undefined, "FAST");
        });
      });

      pdf.save(`Zakupy_wg_okresow_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Wystąpił błąd podczas generowania PDF.");
    } finally {
      disableExportMode();
      setIsDownloading(false);
    }
  };

  const handleDownloadPNG = async () => {
    setIsDownloading(true);
    enableExportMode();
    try {
      const zip = new JSZip();

      for (const c of COMPONENTS_FOR_REPORT) {
        if (!c.ref?.current) continue;
        c.ref.current.scrollIntoView({ block: "center" });
        const restore = hideCardControlsTemporarily(c.ref.current);
        await wait(200);
        const canvas = await html2canvas(c.ref.current, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          ignoreElements: (node) => {
            if (!(node instanceof Element)) return false;
            return node.matches('[data-export-hide="true"]');
          },
        });
        restore();

        const imgData = canvas.toDataURL("image/png");
        zip.file(`${c.title.replace(/\s+/g, "_")}.png`, imgData.split("base64,")[1], { base64: true });
      }

      const blob = await zip.generateAsync({ type: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Zakupy_wg_okresow_${new Date().toISOString().slice(0, 10)}.zip`;
      link.click();
    } catch (err) {
      console.error(err);
      alert("Wystąpił błąd podczas generowania PNG.");
    } finally {
      disableExportMode();
      setIsDownloading(false);
    }
  };

  return (
    <>
      <ExportModeStyles />

      <div style={{ display: "flex", height: "100vh" }}>
        <Sidebar />

        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          {/* Top bar inside the content column */}
          <TopBar />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 20px",
              fontSize: "1.2rem",
              fontWeight: "bold",
              color: "#9848c7",
              marginBottom: "8px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {icon} {name}
            </div>

            {/* License-gated export buttons */}
            <DownloadButtons
              licenseType={licenseType}
              onDownloadPDF={handleDownloadPDF}
              onDownloadPNG={handleDownloadPNG}
              disabled={isDownloading}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              position: "relative",
              marginTop: "2px",
              color: "#444",
              fontSize: "1rem",
              padding: "5px 20px 10px 20px",
              maxWidth: "90%",
              lineHeight: 1.5,
            }}
          />

          <div style={{ padding: "20px", backgroundColor: "#f5f5f5", flex: 1, overflow: "auto" }}>
            <div className="dashboard-grid" style={{ marginBottom: "20px" }}>
              <SectionTitle
                inGrid
                title="Przegląd zakupów według miesięcy, tygodni i dni – porównania okresowe, analiza sezonowości i wykrywanie anomalii."
              />

              <div className="card-span-4" style={cardStyle} ref={monthlySalesRef}>
                <MonthlyPurchaseComparison data={data} />
              </div>
              <div className="card-span-4" style={cardStyle} ref={quarterlySalesRef}>
                <QuarterlyPurchaseComparison data={data} />
              </div>
              <div className="card-span-4" style={cardStyle} ref={weekDetailsRef}>
                <WeekPurchaseDetailsChart data={data} />
              </div>
            </div>

            <div className="dashboard-grid">
              <div className="card-span-6" style={cardStyle} ref={weekComparisonRef}>
                <WeekPurchaseComparisonChart data={data} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={dailyComparisonRef}>
                <DailyPurchaseComparisonChart data={data} />
              </div>

              <SectionTitle
                inGrid
                title="Szczegółowa analiza miesięcy w danym roku, najlepsze i najgorsze miesiące roku, wraz z porównaniem danego miesiąca z poprzednim (m/m) i z tym samym miesiącem w poprzednim roku."
              />

              <div className="card-span-6" style={cardStyle} ref={tabelaRef}>
                <TabelaPorownaniaMiesiecyZakupy data={data} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={monthlyReportRef}>
                <ZakupyWedlugMiesiecyReport data={data} />
              </div>

              <SectionTitle
                inGrid
                title="Porównaj wartości dzienne zakupów ze średnią wg tygodnia i dnia danego miesiąca; obserwuj transakcje znacząco odbiegające od średniej."
              />

              <div className="card-span-6" style={cardStyle} ref={nietypoweRef}>
                <IdentyfikacjaNietypowychZakupow data={data} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={averageSalesRef}>
                <AveragePurchasesByWeekday data={data} />
              </div>

              <div className="card-span-12" style={cardStyle} ref={scatterPlotRef}>
                <ScatterPlotZakupy data={data} />
              </div>

              <SectionTitle
                inGrid
                title="Struktura zakupów (Kraj / UE / Eksport) w ujęciu miesięcznym i tygodniowym"
              />

              <div className="card-span-6" style={cardStyle} ref={strukturaRef}>
                <StrukturaZakupowChart data={data} />
              </div>
              <div className="card-span-6" style={cardStyle} ref={weeklyTrendsRef}>
                <ZakupyTygodnioweZTrendami data={data} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {isDownloading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.8)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.5rem",
            fontWeight: "bold",
            color: "#333",
          }}
        >
          Poczekaj, trwa generowanie raportu... ⏳
        </div>
      )}
    </>
  );
};

export default ZakupyDzien;
